"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_SADE_SATI_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_SADE_SATI_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import CalculatorReportBridge from "@/components/CalculatorReportBridge";
import RatingPrompt from "@/components/RatingPrompt";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { getCurrentTransits, type ActiveTransit } from "@/lib/api/transits";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { getTodayISTDateString } from "@/lib/ist-date";
import { getBrowserQueryClient } from "@/lib/query-client";
import { SADE_SATI_PAGE_HEADLINE } from "./SadeSati.copy";
import { SadeSatiPhases } from "./_phases";
import { RashiPicker } from "./_rashi-picker";
import { SadeSatiRemedies } from "./_remedies";

export default function SadeSatiClient({
  defaultLanguage = "en",
  crossLinks,
}: {
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const hi = defaultLanguage === "hi";
  const lookup = useSadeSatiLookup(defaultLanguage);

  return (
    <div className="intent-interface-diagnostic-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        <SadeSatiHeader hi={hi} />
        <CurrentSaturnPosition
          saturnTransit={lookup.saturnTransit}
          calculationDate={lookup.dateStr}
          hi={hi}
        />
        <RashiPicker
          selectedRashi={lookup.selectedRashi}
          onSelect={lookup.setSelectedRashi}
          sadeSati={lookup.selectedStatus}
          saturnTransit={lookup.saturnTransit}
          isLoading={lookup.isLoading}
          language={defaultLanguage}
        />
        <SelectedResultOffer visible={lookup.hasSelectedResult} language={defaultLanguage} />
        <SelectedResultRating visible={lookup.hasSelectedResult} hi={hi} />

        <SadeSatiPhases />
        <SadeSatiRemedies />
        <SadeSatiSeoCard />
        {crossLinks}
      </div>
    </div>
  );
}

function useSadeSatiLookup(language: "en" | "hi") {
  const [selectedRashi, setSelectedRashi] = useState<string | null>(null);
  const trackedRashiRef = useRef<string | null>(null);
  const dateStr = useMemo(() => getTodayISTDateString(), []);
  const queryRashi = selectedRashi ?? "mesh";
  const { data: transitData, isLoading } = useQuery(
    {
      queryKey: ["transits", "current", queryRashi, dateStr],
      queryFn: () => getCurrentTransits(queryRashi, dateStr),
      staleTime: 30 * 60 * 1000,
      retry: 1,
    },
    getBrowserQueryClient()
  );
  const saturnTransit = transitData?.active_transits.find((entry) => entry.planet_id === "saturn");
  const hasSelectedResult = Boolean(selectedRashi && transitData?.moon_rashi === selectedRashi);
  const selectedStatus = hasSelectedResult ? transitData?.sade_sati : undefined;

  useEffect(() => {
    if (!selectedRashi || !hasSelectedResult || trackedRashiRef.current === selectedRashi) return;
    trackedRashiRef.current = selectedRashi;
    trackCalculatorUsed({
      calculator: "sade-sati",
      language,
      source_path: language === "hi" ? "/hi/sade-sati" : "/sade-sati",
    });
  }, [hasSelectedResult, language, selectedRashi]);

  return {
    dateStr,
    hasSelectedResult,
    isLoading,
    saturnTransit: saturnTransit ?? null,
    selectedRashi,
    selectedStatus,
    setSelectedRashi,
  };
}

function SelectedResultOffer({ visible, language }: { visible: boolean; language: "en" | "hi" }) {
  if (!visible) return null;
  const hi = language === "hi";
  return (
    <CalculatorReportBridge
      planId="sade_sati"
      source={hi ? "/hi/sade-sati" : "/sade-sati"}
      intent="remedy_dosha"
      placement={
        hi
          ? RUNTIME_OFFER_PLACEMENT_HI_SADE_SATI_REPORT_BRIDGE
          : RUNTIME_OFFER_PLACEMENT_SADE_SATI_REPORT_BRIDGE
      }
      title={hi ? "Personal Sade Sati Report paayein" : "Get your personal Sade Sati Report"}
      body={
        hi
          ? "Live result Moon-sign phase dikhata hai. Private report me natal Shani, dasha context, pressure areas aur chart-specific upay milte hain."
          : "The live result checks your Moon-sign phase. The private report adds natal Shani, dasha context, pressure areas, and chart-specific upay."
      }
      cta={hi ? "Sade Sati Report paayein" : "Get Sade Sati Report"}
      language={language}
    />
  );
}

function SelectedResultRating({ visible, hi }: { visible: boolean; hi: boolean }) {
  if (!visible) return null;
  return (
    <div className="mt-6">
      <RatingPrompt
        source={"tool_result" as never}
        mode="thumbs+reasons"
        reasons={RESULT_REASONS}
        prompt={hi ? "Kya yeh Sade Sati check sahi tha?" : "Was this Sade Sati check accurate?"}
        context={{
          surface: "sade_sati_result",
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }}
      />
    </div>
  );
}

function SadeSatiHeader({ hi }: { hi: boolean }) {
  return (
    <div className="mb-10 text-center">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-ish-accent">
        Saturn Transit Calculator
      </p>
      <h1 className="font-serif text-[28px] font-bold leading-tight text-ish-dark sm:text-[36px]">
        {hi ? "Sade Sati ka live phase check karein" : SADE_SATI_PAGE_HEADLINE}
      </h1>
      <p className="mx-auto mt-3 max-w-lg text-[14px] text-ish-text-muted">
        {hi
          ? "Apni Janma Rashi ko Shani ki live engine-computed position se milayein."
          : "Match your Moon sign against Shani's live engine-computed position."}
      </p>
    </div>
  );
}

function CurrentSaturnPosition({
  saturnTransit,
  calculationDate,
  hi,
}: {
  saturnTransit: ActiveTransit | null;
  calculationDate: string;
  hi: boolean;
}) {
  return (
    <div className="card-sacred mb-6 p-6 text-center sm:p-8">
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-ish-text-faint">
        {hi ? "Shani ki live engine position" : "Live engine-computed Saturn position"}
      </span>
      {saturnTransit ? (
        <>
          <span className="block font-serif text-[28px] font-bold text-ish-dark">
            {saturnTransit.current_rashi_name_en} ({saturnTransit.current_rashi_name_hi})
          </span>
          <span className="mt-1 block text-[13px] text-ish-text-muted">
            ♄ {saturnTransit.rashi_degree.toFixed(1)}° · {saturnTransit.nakshatra} ·{" "}
            {saturnTransit.is_retrograde ? "Retrograde" : "Direct"}
          </span>
          <span className="mt-2 block text-[12px] text-ish-text-faint">{calculationDate}</span>
        </>
      ) : (
        <span className="text-[14px] text-ish-text-muted">
          {hi
            ? "Live Shani position calculate ho rahi hai…"
            : "Calculating Saturn's live position…"}
        </span>
      )}
    </div>
  );
}

function SadeSatiSeoCard() {
  return (
    <div className="card-sacred mt-12 p-6 sm:p-8">
      <h2 className="mb-4 font-serif text-[20px] font-bold text-ish-dark">About Sade Sati</h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          <strong className="text-ish-dark">Sade Sati</strong> is Saturn&apos;s transit through the
          12th, 1st, and 2nd signs counted from the natal Moon. The live engine result above uses
          your selected Janma Rashi instead of a year-specific hand-maintained status table.
        </p>
        <p>
          Don&apos;t know your Moon sign? Use the{" "}
          <Link href="/rashi-calculator" className="font-semibold text-ish-accent">
            Rashi Calculator
          </Link>
          . For the deeper result, your personal Sade Sati Report combines the live transit with
          natal Saturn and the active dasha from your full birth chart.
        </p>
      </div>
    </div>
  );
}
